import React, { useState, useEffect, useRef } from 'react';

const PatternRecognitionGame = ({ gameSession, onGameComplete, onBack }) => {
  const [currentPattern, setCurrentPattern] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [gameState, setGameState] = useState('ready'); // 'ready', 'playing', 'finished'
  const [gameData, setGameData] = useState({
    patterns: [],
    answers: [],
    startTime: null,
    endTime: null
  });
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [targetQuestions] = useState(10); // Fixed number of patterns

  const answerInputRef = useRef(null);

  // Australian Curriculum-aligned grade mappings for patterns
  const GRADE_PATTERN_MAPPINGS = {
    'Year 3': {
      patterns: { types: ['arithmetic'], maxNumber: 50, maxStep: 5 },
      description: 'Simple counting patterns and number sequences'
    },
    'Year 4': {
      patterns: { types: ['arithmetic', 'simple_geometric'], maxNumber: 100, maxStep: 10 },
      description: 'Skip counting patterns and simple sequences'
    },
    'Year 5': {
      patterns: { types: ['arithmetic', 'geometric'], maxNumber: 200, maxStep: 15 },
      description: 'Growing patterns and number relationships'
    },
    'Year 6': {
      patterns: { types: ['arithmetic', 'geometric', 'fibonacci'], maxNumber: 500, maxStep: 25 },
      description: 'Complex patterns and number relationships'
    },
    'Year 7': {
      patterns: { types: ['arithmetic', 'geometric', 'fibonacci', 'squares'], maxNumber: 1000, maxStep: 50 },
      description: 'Advanced patterns including squares and special sequences'
    },
    'Year 8': {
      patterns: { types: ['arithmetic', 'geometric', 'fibonacci', 'squares', 'cubes'], maxNumber: 2000, maxStep: 100 },
      description: 'Complex sequences including cubes and advanced patterns'
    },
    'Year 9': {
      patterns: { types: ['arithmetic', 'geometric', 'fibonacci', 'squares', 'cubes', 'mixed'], maxNumber: 5000, maxStep: 200 },
      description: 'All pattern types including mixed and interlaced sequences'
    },
    'Year 10': {
      patterns: { types: ['arithmetic', 'geometric', 'fibonacci', 'squares', 'cubes', 'mixed'], maxNumber: 10000, maxStep: 500 },
      description: 'Advanced mathematical pattern recognition'
    },
    'Year 11': {
      patterns: { types: ['arithmetic', 'geometric', 'fibonacci', 'squares', 'cubes', 'mixed'], maxNumber: 20000, maxStep: 1000 },
      description: 'Complex sequences for advanced mathematics'
    },
    'Year 12': {
      patterns: { types: ['arithmetic', 'geometric', 'fibonacci', 'squares', 'cubes', 'mixed'], maxNumber: 50000, maxStep: 2000 },
      description: 'University-level pattern analysis'
    }
  };

  // Grade-aware pattern enhancement
  const enhancePatternWithGrade = (baseSettings, gradeLevel) => {
    const gradeMapping = GRADE_PATTERN_MAPPINGS[gradeLevel];
    if (!gradeMapping) return baseSettings; // Fallback to existing behavior

    const gradePatterns = gradeMapping.patterns;

    // Filter pattern types appropriate for grade level
    const filteredPatternTypes = baseSettings.patternTypes.filter(type =>
      gradePatterns.types.includes(type)
    );

    return {
      ...baseSettings,
      maxNumber: Math.min(baseSettings.maxNumber, gradePatterns.maxNumber),
      patternTypes: filteredPatternTypes.length > 0 ? filteredPatternTypes : baseSettings.patternTypes,
      gradeInfo: gradeMapping.description
    };
  };

  // Enhanced grade-aware difficulty settings
  const getDifficultySettings = () => {
    const difficulty = gameSession?.difficulty || 'easy';
    const gradeLevel = gameSession?.grade_level;

    // Get traditional difficulty settings (existing behavior)
    let baseSettings;
    switch (difficulty) {
      case 'easy':
        baseSettings = {
          complexity: 1,
          maxNumber: 20,
          patternTypes: ['arithmetic', 'simple_geometric']
        };
        break;
      case 'medium':
        baseSettings = {
          complexity: 2,
          maxNumber: 50,
          patternTypes: ['arithmetic', 'geometric', 'fibonacci', 'squares']
        };
        break;
      case 'hard':
        baseSettings = {
          complexity: 3,
          maxNumber: 100,
          patternTypes: ['arithmetic', 'geometric', 'fibonacci', 'squares', 'cubes', 'mixed']
        };
        break;
      default:
        baseSettings = { complexity: 1, maxNumber: 20, patternTypes: ['arithmetic'] };
    }

    // Enhance with grade level if available (progressive enhancement)
    if (gradeLevel) {
      return enhancePatternWithGrade(baseSettings, gradeLevel);
    }

    // Fallback to existing behavior for users without grade
    return baseSettings;
  };

  // Generate random number within range
  const randomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  // Generate different types of patterns
  const generatePattern = () => {
    const { complexity, maxNumber, patternTypes } = getDifficultySettings();
    const patternType = patternTypes[Math.floor(Math.random() * patternTypes.length)];
    
    let sequence = [];
    let missingIndex;
    let correctAnswer;
    let description = '';

    switch (patternType) {
      case 'arithmetic':
        const diff = randomNumber(1, 10);
        const start = randomNumber(1, 20);
        sequence = Array.from({ length: 5 }, (_, i) => start + i * diff);
        missingIndex = randomNumber(1, 4); // Don't make first one missing
        description = `Add ${diff} each time`;
        break;
        
      case 'simple_geometric':
      case 'geometric':
        const multiplier = randomNumber(2, 4);
        const startGeo = randomNumber(1, 5);
        sequence = Array.from({ length: 5 }, (_, i) => startGeo * Math.pow(multiplier, i));
        missingIndex = randomNumber(1, 4);
        description = `Multiply by ${multiplier} each time`;
        break;
        
      case 'fibonacci':
        sequence = [1, 1, 2, 3, 5];
        missingIndex = randomNumber(2, 4);
        description = 'Add the two previous numbers';
        break;
        
      case 'squares':
        const startSquare = randomNumber(1, 5);
        sequence = Array.from({ length: 5 }, (_, i) => Math.pow(startSquare + i, 2));
        missingIndex = randomNumber(1, 4);
        description = 'Perfect squares';
        break;
        
      case 'cubes':
        const startCube = randomNumber(1, 4);
        sequence = Array.from({ length: 5 }, (_, i) => Math.pow(startCube + i, 3));
        missingIndex = randomNumber(1, 4);
        description = 'Perfect cubes';
        break;
        
      case 'mixed':
        // Alternating pattern
        const base1 = randomNumber(1, 10);
        const base2 = randomNumber(1, 10);
        const step1 = randomNumber(2, 5);
        const step2 = randomNumber(2, 5);
        sequence = [base1, base2, base1 + step1, base2 + step2, base1 + step1 * 2];
        missingIndex = randomNumber(2, 4);
        description = 'Two interlaced patterns';
        break;
        
      default:
        // Default to arithmetic
        const defaultDiff = randomNumber(1, 5);
        const defaultStart = randomNumber(1, 10);
        sequence = Array.from({ length: 5 }, (_, i) => defaultStart + i * defaultDiff);
        missingIndex = randomNumber(1, 4);
        description = `Add ${defaultDiff} each time`;
    }

    correctAnswer = sequence[missingIndex];
    
    return {
      sequence: sequence.map((num, index) => index === missingIndex ? null : num),
      correctAnswer,
      missingIndex,
      patternType,
      description,
      hint: description,
      timestamp: Date.now()
    };
  };

  // Start the game
  const startGame = () => {
    setGameState('playing');
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setGameData({
      patterns: [],
      answers: [],
      startTime: Date.now(),
      endTime: null
    });
    
    const firstPattern = generatePattern();
    setCurrentPattern(firstPattern);
  };

  // Submit answer
  const submitAnswer = () => {
    if (!currentPattern || userAnswer.trim() === '' || gameState !== 'playing') return;

    const userNum = parseInt(userAnswer.trim());
    const correct = userNum === currentPattern.correctAnswer;
    
    // Update game data
    setGameData(prev => ({
      ...prev,
      patterns: [...prev.patterns, currentPattern],
      answers: [...prev.answers, {
        userAnswer: userNum,
        correctAnswer: currentPattern.correctAnswer,
        isCorrect: correct,
        timeSpent: Date.now() - currentPattern.timestamp
      }]
    }));

    setQuestionsAnswered(prev => prev + 1);
    if (correct) {
      setCorrectAnswers(prev => prev + 1);
    }

    // Show feedback
    setIsCorrect(correct);
    setShowFeedback(true);
    
    // Check if game should end
    if (questionsAnswered + 1 >= targetQuestions) {
      setTimeout(() => endGame(), 1500);
      return;
    }
    
    // Clear input and generate next pattern after brief feedback
    setTimeout(() => {
      setShowFeedback(false);
      setUserAnswer('');
      
      if (gameState === 'playing') {
        const nextPattern = generatePattern();
        setCurrentPattern(nextPattern);
        
        // Focus back to input
        if (answerInputRef.current) {
          answerInputRef.current.focus();
        }
      }
    }, 1200);
  };

  // End the game
  const endGame = () => {    
    setGameState('finished');
    
    const finalGameData = {
      ...gameData,
      endTime: Date.now()
    };
    
    const totalTime = finalGameData.endTime - finalGameData.startTime;
    const score = questionsAnswered > 0 ? Math.round((correctAnswers / questionsAnswered) * 100) : 0;
    
    const results = {
      score,
      timeTaken: Math.floor(totalTime / 1000), // in seconds
      questionsAttempted: questionsAnswered,
      correctAnswers,
      gameData: finalGameData,
      pointsEarned: correctAnswers * 5 // 5 points per correct pattern
    };
    
    onGameComplete(results);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && gameState === 'playing') {
      submitAnswer();
    }
  };

  // Focus input when pattern changes
  useEffect(() => {
    if (gameState === 'playing' && answerInputRef.current) {
      answerInputRef.current.focus();
    }
  }, [currentPattern, gameState]);

  if (gameState === 'ready') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-400 to-indigo-500 p-8 text-white text-center">
            <div className="text-5xl mb-4">🧩</div>
            <h1 className="text-3xl font-bold mb-2">Number Patterns</h1>
            <p className="text-purple-100">
              Difficulty: <span className="font-semibold capitalize">{gameSession?.difficulty}</span>
            </p>
          </div>

          {/* Instructions */}
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Game Instructions</h2>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-4 text-purple-600 font-bold">1</div>
                <div>Find the missing number in each sequence</div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-4 text-purple-600 font-bold">2</div>
                <div>Look for patterns like <strong>+3</strong>, <strong>×2</strong>, or <strong>squares</strong></div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-4 text-purple-600 font-bold">3</div>
                <div>Complete <strong>{targetQuestions} patterns</strong> as accurately as possible</div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-4 text-purple-600 font-bold">4</div>
                <div>Earn <strong>5 points per correct answer</strong></div>
              </div>
            </div>

            {/* Difficulty Info */}
            <div className="bg-purple-50 rounded-lg p-4 mb-8">
              <h3 className="font-semibold text-purple-900 mb-2">
                {gameSession?.difficulty?.charAt(0).toUpperCase() + gameSession?.difficulty?.slice(1)} Mode
                {gameSession?.grade_level && (
                  <span className="text-sm font-normal text-purple-700 ml-2">
                    • {gameSession.grade_level}
                  </span>
                )}
              </h3>
              <p className="text-purple-800 text-sm">
                {getDifficultySettings().patternTypes.length} pattern types,
                numbers up to {getDifficultySettings().maxNumber}
              </p>
              {getDifficultySettings().gradeInfo && (
                <p className="text-purple-700 text-xs mt-1">
                  Australian Curriculum: {getDifficultySettings().gradeInfo}
                </p>
              )}
            </div>

            {/* Sample Pattern */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-3">Example Pattern:</h3>
              <div className="flex items-center justify-center space-x-4 text-2xl font-mono">
                <span className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">2</span>
                <span className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">4</span>
                <span className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center border-2 border-red-400">?</span>
                <span className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">8</span>
                <span className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">10</span>
              </div>
              <p className="text-center text-gray-600 text-sm mt-2">Answer: 6 (add 2 each time)</p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-4">
              <button
                onClick={onBack}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-medium transition-colors"
              >
                Back to Games
              </button>
              <button
                onClick={startGame}
                className="flex-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white py-3 px-8 rounded-lg font-medium transition-all duration-200 transform hover:scale-105"
              >
                Start Game! 🚀
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'playing') {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Game Header */}
          <div className="bg-gradient-to-r from-purple-400 to-indigo-500 p-6 text-white">
            <div className="flex justify-between items-center">
              <div className="text-center">
                <div className="text-2xl font-bold">{questionsAnswered}/{targetQuestions}</div>
                <div className="text-sm text-purple-100">Questions</div>
              </div>
              
              <div className="text-4xl">🧩</div>
              
              <div className="text-center">
                <div className="text-2xl font-bold">{correctAnswers}/{questionsAnswered}</div>
                <div className="text-sm text-purple-100">Correct</div>
              </div>
            </div>
          </div>

          {/* Pattern Display */}
          <div className="p-12 text-center">
            {showFeedback ? (
              <div className={`text-4xl font-bold mb-8 ${isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                {isCorrect ? '✅ Excellent!' : '❌ Not quite'}
                {!isCorrect && currentPattern && (
                  <div className="text-2xl text-gray-600 mt-4">
                    Answer: {currentPattern.correctAnswer}
                  </div>
                )}
                {currentPattern?.hint && (
                  <div className="text-lg text-gray-500 mt-2">
                    Pattern: {currentPattern.hint}
                  </div>
                )}
              </div>
            ) : (
              <>
                <h3 className="text-xl text-gray-700 mb-8">Find the missing number:</h3>
                
                {/* Pattern Sequence */}
                <div className="flex items-center justify-center space-x-4 mb-8">
                  {currentPattern?.sequence.map((num, index) => (
                    <div key={index} className="relative">
                      {num === null ? (
                        <div className="w-16 h-16 bg-red-100 border-2 border-red-400 rounded-xl flex items-center justify-center text-2xl font-bold text-red-600">
                          ?
                        </div>
                      ) : (
                        <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center text-2xl font-bold text-purple-800">
                          {num}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Answer Input */}
                <div className="mb-8">
                  <input
                    ref={answerInputRef}
                    type="number"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="text-3xl text-center border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:border-purple-600 font-mono w-32"
                    placeholder="?"
                    autoFocus
                  />
                </div>
                
                <button
                  onClick={submitAnswer}
                  disabled={!userAnswer.trim()}
                  className="bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white py-3 px-8 rounded-lg font-medium transition-colors"
                >
                  Submit Answer
                </button>

                {/* Hint */}
                {currentPattern?.hint && (
                  <div className="mt-6 p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-700">
                      💡 Hint: Look for {currentPattern.hint.toLowerCase()}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Progress Bar */}
          <div className="px-6 pb-6">
            <div className="bg-gray-200 rounded-full h-3">
              <div 
                className="bg-purple-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${(questionsAnswered / targetQuestions) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600 mt-2">
              <span>Progress: {questionsAnswered}/{targetQuestions}</span>
              <span>Accuracy: {questionsAnswered > 0 ? Math.round((correctAnswers / questionsAnswered) * 100) : 0}%</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null; // Game finished state is handled by parent component
};

export default PatternRecognitionGame;